/**
 * Flow Queuing System — the per-item DESTRUCTIVE-OPERATION SERIAL QUEUE
 * (D12 Codex round-3 fixes #1, #2 and #3).
 *
 * ── What this replaces, and why a third primitive was needed ──────────────
 *
 * The queue already had two per-item concurrency primitives, and round 2
 * added a third:
 *
 *  - `useSendLock`'s `busyItems` — SEND-pipeline mutual exclusion. Claimed
 *    check-and-set through a ref (two clicks in one event batch can't both
 *    pass), mirrored into state so every surface renders its field/button
 *    locks off it. Its contract is "one send pipeline per item at a time".
 *  - `EnrollQueueContext`'s `writeBarriersRef` — an AUTOSAVE WRITE GATE.
 *    While held, `autosavePatch` refuses to schedule and `dispatchAutosave`
 *    refuses to dispatch for that item, so nothing the client queues during
 *    a reset/reassignment window can reach the server.
 *
 * Round 3's finding was that neither is an ORDERING primitive. The write
 * barrier is a refcount: it correctly stops a premature release when two
 * destructive operations for one item overlap, but it does not make the two
 * operations take turns, and it does not stop a Send from starting while it
 * is held. Three separate reported bugs were all that one gap:
 *
 *  1. **Send shipped stale copy.** The (now-deleted, terra-ohe.7) client-side
 *     materialization function skipped a barred item and `flushAutosave`
 *     treated a barrier-blocked dispatch as a successful drain, so the send
 *     pipeline POSTed whatever the server happened to hold rather than the
 *     staged value the UI was showing. Reset/reassignment set no `itemBusy`,
 *     so the Send button stayed clickable throughout.
 *  2. **Reset raced reassignment.** Both fire a DESTRUCTIVE PATCH; barred or
 *     not, they were unsequenced. A Reset initiated while the row still
 *     showed play A could land AFTER a reassignment to play B and write its
 *     stale, captured-under-A `baseSender` onto the item now under B.
 *  3. **Double reassignment misclassified.** `assignPlay`'s `playChanged`
 *     predicate reads the query cache, which the first (still in-flight)
 *     reassignment has not updated yet — so a rapid A→B→A sequence saw
 *     cached play=A for the second call and computed a false "same-play
 *     no-op", skipping every side effect a real B→A move needs.
 *
 * One mechanism fixes all three: destructive per-item operations genuinely
 * WAIT for each other, and "an operation is queued or running for this item"
 * is a RENDERED fact that the send lock and every send affordance can gate
 * on.
 *
 * ── The contract ─────────────────────────────────────────────────────────
 *
 * - `runItemOp(itemId, op)` appends `op` to `itemId`'s chain. It does not
 *   start until every previously-queued op for the SAME id has fully settled
 *   (including its server round trip). Different ids never block each other.
 * - The returned promise settles with `op`'s own result/rejection, so callers
 *   keep their existing error propagation. The internal chain tail is
 *   settle-CHAINED, so one op's rejection never poisons the next.
 * - `isItemOpPending` is a synchronous REF read (the `useSendLock`
 *   check-and-set precedent): a Send click landing in the same event batch as
 *   an op must be refused, which a state read could not guarantee.
 * - `opPendingItems` is the rendered mirror, folded into the context's
 *   `isItemBusy` so a row mid-reset/mid-reassignment locks its fields and
 *   disables its Send in EVERY mounted surface — finding #1's visible half.
 * - `awaitItemOps` lets a non-op caller wait the chain out. `flushAutosave`
 *   uses it: a flush must not report success while the write barrier that
 *   silently blocked its dispatch is still held.
 *
 * Deliberately NOT merged into `useSendLock`: that lock's semantics are
 * "refuse a second pipeline" (a `begin` that returns `false`), while this
 * one's are "queue and wait" (an `await` that always eventually runs). One
 * hook doing both would need every caller to say which behavior it wanted.
 *
 * Ref-based bookkeeping with a state mirror rather than state alone: the
 * depth map is read and written across awaits and must never be a render
 * behind. No mount-lifetime flags, so nothing for StrictMode's
 * setup→cleanup→setup replay to leave stuck (CLAUDE.md "StrictMode-Resilient
 * Effects").
 */
import { useCallback, useRef, useState } from 'react';

export interface ItemOpQueue {
  /**
   * Runs `op` for `itemId`, serialized against every other op queued for the
   * same id. Resolves/rejects with `op`'s own outcome.
   */
  runItemOp: <T>(itemId: number, op: () => Promise<T>) => Promise<T>;
  /**
   * Synchronous truth: is an op queued or running for `itemId`? Read from the
   * ref, never the state mirror — see the module doc comment.
   */
  isItemOpPending: (itemId: number) => boolean;
  /**
   * Resolves once everything currently queued for `itemId` has settled.
   * Never rejects (the chain is settle-chained). A no-op when the item has
   * no chain.
   */
  awaitItemOps: (itemId: number) => Promise<void>;
  /** Rendered mirror of the pending set — folded into `isItemBusy`. */
  opPendingItems: ReadonlySet<number>;
}

export function useItemOpQueue(): ItemOpQueue {
  /** item id -> the settle-chained tail of that item's op chain. */
  const tailsRef = useRef<Map<number, Promise<void>>>(new Map());
  /** item id -> how many ops are queued or running. Absent means zero. */
  const depthsRef = useRef<Map<number, number>>(new Map());
  const [opPendingItems, setOpPendingItems] = useState<ReadonlySet<number>>(() => new Set());

  const isItemOpPending = useCallback(
    (itemId: number) => (depthsRef.current.get(itemId) ?? 0) > 0,
    [],
  );

  const runItemOp = useCallback(<T,>(itemId: number, op: () => Promise<T>): Promise<T> => {
    // Claimed SYNCHRONOUSLY, before the first await — the whole point is that
    // a Send click in the same event batch already sees this item as pending.
    const depth = (depthsRef.current.get(itemId) ?? 0) + 1;
    depthsRef.current.set(itemId, depth);
    if (depth === 1) {
      setOpPendingItems(prev => {
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });
    }
    // The previous tail is already settle-chained, so `previous.then(op)`
    // starts `op` regardless of how the predecessor ended — a failed reset
    // must not strand a queued reassignment forever.
    const previous = tailsRef.current.get(itemId) ?? Promise.resolve();
    const result = previous.then(op);
    const tail: Promise<void> = result.then(() => undefined, () => undefined).then(() => {
      const remaining = (depthsRef.current.get(itemId) ?? 1) - 1;
      if (remaining > 0) {
        depthsRef.current.set(itemId, remaining);
        return;
      }
      depthsRef.current.delete(itemId);
      // Identity-guarded: a newer op may already have replaced the tail, and
      // dropping THAT would let the next call start unsequenced.
      if (tailsRef.current.get(itemId) === tail) tailsRef.current.delete(itemId);
      setOpPendingItems(prev => {
        if (!prev.has(itemId)) return prev;
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    });
    tailsRef.current.set(itemId, tail);
    return result;
  }, []);

  const awaitItemOps = useCallback(async (itemId: number): Promise<void> => {
    // Loops rather than awaiting once: an op queued WHILE we were waiting
    // installs a newer tail, and "everything queued for this item has
    // settled" has to include it — otherwise a flush could resolve into the
    // middle of a second destructive operation. Terminates because the tail
    // identity stops changing once the chain drains (its own bookkeeping
    // deletes the entry).
    let awaited: Promise<void> | undefined;
    for (;;) {
      const tail = tailsRef.current.get(itemId);
      if (tail === undefined || tail === awaited) return;
      awaited = tail;
      await tail;
    }
  }, []);

  return { runItemOp, isItemOpPending, awaitItemOps, opPendingItems };
}
